import type { DisplayRole } from '@/features/auth/roles';

/**
 * The auth slice injected into the TanStack Router context (SCRUM-616). The
 * router is created ONCE with a placeholder context; the live value is injected
 * every render via `<RouterProvider context={{ auth }} />` in App.tsx, sourced
 * from the in-memory Zustand auth store. This keeps the ADR-011 / SCRUM-102
 * token-off-disk posture: no auth state is persisted, and route `beforeLoad`
 * guards read the live in-memory value synchronously (no async loader fetch).
 */
export interface AuthContext {
  isAuthenticated: boolean;
  role: DisplayRole;
}

export interface RouterContext {
  auth: AuthContext;
}

/**
 * Validate the `?redirect=` search param on /login is a SAFE same-origin path
 * before we bounce a just-authenticated user to it (SCRUM-616 validation-pass
 * RT4: open-redirect hardening). Accept only an absolute in-app PATH: it must
 * start with a single '/', and must NOT start with '//' or '/\' (protocol-
 * relative URLs that browsers treat as external hosts). Anything else -> null,
 * and the caller falls back to the role landing.
 */
export function safeRedirectPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (!value.startsWith('/')) return null;
  // Reject protocol-relative ("//evil.com") and backslash tricks ("/\evil.com").
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
}
